import { computed, ref } from 'vue';
import { listCompanies, saveCompany } from '@/api/settingsApi';
import { useSettingsEntityList } from './useSettingsEntityList';

const GROUP_LABELS = {
  w2: 'W-2',
  w9: 'W-9',
  other: 'Other'
};

export function useCompanies() {
  const { afterMutation } = useSettingsEntityList();

  const loading = ref(false);
  const saving = ref(false);
  const error = ref(null);
  const companies = ref({ w2: [], w9: [], other: [] });
  const groups = ref(['w2', 'w9', 'other']);
  const searchQuery = ref('');

  const flatItems = computed(() => {
    const rows = [];
    for (const group of groups.value) {
      const list = companies.value[group] || [];
      for (const item of list) {
        rows.push({
          ...item,
          group,
          groupLabel: GROUP_LABELS[group] || group
        });
      }
    }
    return rows;
  });

  const filteredItems = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return flatItems.value;
    return flatItems.value.filter((row) => {
      const hay = [row.label, row.id, row.group, row.groupLabel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  });

  const countLabel = computed(() => {
    const n = filteredItems.value.length;
    const total = flatItems.value.length;
    if (searchQuery.value.trim() && n !== total) {
      return `${n} of ${total} companies`;
    }
    return `${total} compan${total === 1 ? 'y' : 'ies'}`;
  });

  const groupOptions = computed(() =>
    groups.value.map((g) => ({ label: GROUP_LABELS[g] || g, value: g }))
  );

  async function loadItems() {
    loading.value = true;
    error.value = null;
    try {
      const data = await listCompanies({ includeInactive: true });
      companies.value = {
        w2: Array.isArray(data?.companies?.w2) ? data.companies.w2 : [],
        w9: Array.isArray(data?.companies?.w9) ? data.companies.w9 : [],
        other: Array.isArray(data?.companies?.other) ? data.companies.other : []
      };
      if (Array.isArray(data?.groups) && data.groups.length) {
        groups.value = data.groups;
      }
    } catch (e) {
      error.value = e?.message || 'Failed to load company assignments';
    } finally {
      loading.value = false;
    }
  }

  async function saveItem(payload) {
    saving.value = true;
    error.value = null;
    try {
      const data = await saveCompany(payload);
      await afterMutation(async () => {
        if (data?.companies) {
          companies.value = {
            w2: Array.isArray(data.companies.w2) ? data.companies.w2 : [],
            w9: Array.isArray(data.companies.w9) ? data.companies.w9 : [],
            other: Array.isArray(data.companies.other) ? data.companies.other : []
          };
        } else {
          await loadItems();
        }
      });
      return data?.item || null;
    } catch (e) {
      error.value = e?.message || 'Failed to save company assignment';
      throw e;
    } finally {
      saving.value = false;
    }
  }

  return {
    loading,
    saving,
    error,
    companies,
    groups,
    groupOptions,
    searchQuery,
    filteredItems,
    countLabel,
    loadItems,
    saveItem,
    GROUP_LABELS
  };
}
